// Types
type SubdomainType = (typeof VALID_SUBDOMAINS)[number] | 'default';
type DatabaseTenant = 'cobra' | 'bw' | 'default';

// All valid regional and organizational keys in your ecosystem
const VALID_SUBDOMAINS = [
	'nrw',
	'bw',
	'mv',
	'sn',
	'st',
	'be',
	'bb',
	'hh',
	'nord-hh',
	'ni',
	'th',
	'rp',
	'he',
	'www',
	'beta',
	'dus',
	'bund',
	'bie',
	'koeln',
	'ruhr',
	'kassel',
	'hsk',
	'eus',
	'rheydt',
	'mkr',
	'live',
	'beta-bw',
	'beta-dus',
	'beta-nrw'
] as const;

const TENANT_DATABASE_MAP: Record<SubdomainType, DatabaseTenant> = {
	// Cobra Database Cluster (NRW + Innungen)
	nrw: 'cobra',
	dus: 'cobra',
	koeln: 'cobra',
	ruhr: 'cobra',
	bie: 'cobra',
	kassel: 'cobra',
	hsk: 'cobra',
	eus: 'cobra',
	rheydt: 'cobra',
	mkr: 'cobra',
	beta: 'cobra',
	live: 'cobra',
	www: 'cobra',
	'beta-dus': 'cobra',
	'beta-nrw': 'cobra',

	// Baden-Württemberg Cluster
	bw: 'bw',
	'beta-bw': 'bw',

	// Default or other regional clusters
	be: 'default',
	hh: 'default',
	'nord-hh': 'default',
	ni: 'default',
	bund: 'default',
	bb: 'default',
	he: 'default',
	mv: 'default',
	th: 'default',
	rp: 'default',
	sn: 'default',
	st: 'default',
	default: 'default'
};

/**
 * Extracts the specific organizational subdomain/slug from a hostname.
 */
export const getSubdomain = (hostname: string): SubdomainType => {
	if (!hostname) return 'default';

	// Normalize to strip protocol and port if included
	const cleanedHost = hostname.replace(/^https?:\/\//, '').split(':')[0];

	// Handle custom full domains first
	if (cleanedHost === 'tischlerinnung-duesseldorf.de' || cleanedHost === 'www.tischlerinnung-duesseldorf.de') {
		return 'dus';
	}

	if (cleanedHost === 'tischler.nrw' || cleanedHost === 'www.tischler.nrw') {
		return 'nrw';
	}

	// Handle special default cases
	if (cleanedHost === 'tischler.studio' || cleanedHost === 'localhost') {
		return 'default';
	}

	// Extract subdomain from something like nord.tischler.studio
	const subdomains = cleanedHost.split('.').slice(0, -2);
	const lastSubdomain = subdomains[subdomains.length - 1];

	if ((VALID_SUBDOMAINS as readonly string[]).includes(lastSubdomain)) {
		return lastSubdomain as SubdomainType;
	}

	return 'default';
};

export const getDatabaseTenant = (subdomain: SubdomainType): DatabaseTenant => {
	return TENANT_DATABASE_MAP[subdomain] || 'default';
};

export const getAllowedGroupsForDomain = (subdomain: SubdomainType): string[] => {
	switch (subdomain) {
		case 'nrw':
		case 'dus':
		case 'koeln':
		case 'ruhr':
		case 'bie':
		case 'kassel':
		case 'hsk':
		case 'eus':
		case 'rheydt':
		case 'mkr':
			return ['NRW', 'user', 'editor', 'manager'];
		case 'bw':
			return ['BW', 'user', 'editor'];
		default:
			return ['user'];
	}
};
